import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Book, FileText, Download, School, Library, BookOpen, Globe, GraduationCap } from 'lucide-react';

export default function ResourcesPage() {
  return (
    <div className="@container/resources max-w-6xl mx-auto w-full px-3 py-4 @[740px]/resources:px-4 @[740px]/resources:py-5 @[1100px]/resources:px-6 @[1100px]/resources:py-6 space-y-6 @[740px]/resources:space-y-7 @[1100px]/resources:space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-3 @[740px]/resources:space-y-4">
        <Badge variant="outline" className="mb-2">
          <Library className="mr-1 h-3 w-3" />
          Learning Center
        </Badge>
        <h1 className="text-4xl font-bold">Resources</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Tools and materials to support your spiritual growth and transformation.
        </p>
      </div>

      {/* Devotional Section */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="p-3 rounded-full bg-primary/10">
              <BookOpen className="h-10 w-10 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Daily Devotional</h2>
              <p className="text-muted-foreground">
                Start your day with spiritual nourishment through our daily devotionals. Fresh content 
                delivered to help you grow in your faith journey.
              </p>
            </div>
            <Badge variant="secondary">Daily Content</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Main Resources Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <Book className="h-8 w-8 text-primary mb-2" />
            <CardTitle>Bible Study Materials</CardTitle>
            <CardDescription>
              Deepen your understanding of Scripture
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Access our collection of Bible study guides, devotionals, and study materials.
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Daily devotionals</li>
              <li>• Topical study guides</li>
              <li>• Book-by-book studies</li>
              <li>• Character studies</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <FileText className="h-8 w-8 text-primary mb-2" />
            <CardTitle>Forms & Documents</CardTitle>
            <CardDescription>
              Official church documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Find important forms, policies, and documents for church membership and events.
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Membership forms</li>
              <li>• Ministry enrollment</li>
              <li>• Event registration</li>
              <li>• Policy documents</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <GraduationCap className="h-8 w-8 text-primary mb-2" />
            <CardTitle>Teaching Series</CardTitle>
            <CardDescription>
              Comprehensive courses and teachings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Explore in-depth teaching series on key biblical topics and life principles.
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Leadership development</li>
              <li>• Personal transformation</li>
              <li>• Biblical principles</li>
              <li>• Life skills</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <Download className="h-8 w-8 text-primary mb-2" />
            <CardTitle>Downloads</CardTitle>
            <CardDescription>
              Printable resources and materials
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Download printable resources including worksheets, study guides, and reference materials.
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Study worksheets</li>
              <li>• Prayer guides</li>
              <li>• Notes templates</li>
              <li>• Visual aids</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <School className="h-8 w-8 text-primary mb-2" />
            <CardTitle>Online Courses</CardTitle>
            <CardDescription>
              Structured learning programs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Enroll in our online courses for structured learning and certification.
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Leadership institute</li>
              <li>• Discipleship tracks</li>
              <li>• Ministry training</li>
              <li>• Personal development</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <Globe className="h-8 w-8 text-primary mb-2" />
            <CardTitle>Recommended Reading</CardTitle>
            <CardDescription>
              Books and publications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Discover books authored by Dr. David Ogbueli and recommended reading list.
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• Transformational leadership</li>
              <li>• Personal development</li>
              <li>• Church growth</li>
              <li>• National transformation</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Leadership Institute Callout */}
      <Card className="border-dashed">
        <CardContent className="p-6 text-center">
          <GraduationCap className="h-10 w-10 text-primary mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">Dominion Leadership Institute</h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            Take your learning further with our structured leadership development programs. 
            Raising and developing sons and daughters in Christ.
          </p>
          <Badge variant="outline">Learn More</Badge>
        </CardContent>
      </Card>
    </div>
  );
}
